import type React from "react";
import { useState, useEffect, useMemo } from "react";
import { Home, Menu, X, User, MessageCircleHeart } from "lucide-react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import UserProfile from "../admin/components/sidebar/UserProfile";

interface UserDetails {
  name: string;
  photo: string | null;
  email: string;
}

export default function Layout() {
  const [isOpen, setIsOpen] = useState(false);
  const [greeting, setGreeting] = useState("");
  const [userName, setUserName] = useState("User");
  const [userPhoto, setUserPhoto] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState("user@example.com");
  const [userRole, setUserRole] = useState<string | null>(null);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const hasDetails = localStorage.getItem("details");
    const hasToken = localStorage.getItem("_token");
    const hasRole = localStorage.getItem("role");

    setUserRole(hasRole);

    if (!hasDetails || !hasToken) {
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    const stored = localStorage.getItem("details");
    if (!stored) return;
    try {
      const parsed: UserDetails = JSON.parse(stored);
      setUserName(parsed.name || "User");
      setUserPhoto(parsed.photo || null);
      setUserEmail(parsed.email || "user@example.com");
    } catch (error) {
      console.error("Failed to parse user details from localStorage:", error);
    }
  }, []);

  useEffect(() => {
    const hour = new Date().getHours();
    let greet = "Good morning";
    if (hour >= 12 && hour < 17) greet = "Good afternoon";
    else if (hour >= 17) greet = "Good evening";
    const firstName = userName.split(" ")[0];
    setGreeting(`${greet}, ${firstName}`);
  }, [userName]);

  const isActive = (href: string) => location.pathname === href;

  const navLinks = useMemo(
    () => [
      { href: "/dashboard", label: "Home", icon: Home },
      ...(userRole === "admin"
        ? [{ href: "/dashboard/alumnis", label: "Alumnis", icon: User }]
        : []),
      { href: "/dashboard/posts", label: "Posts", icon: MessageCircleHeart },
    ],
    [userRole]
  );

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-zinc-200">
        <div className="flex items-center justify-between h-16 px-4 md:px-6">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="hidden md:block text-sm font-medium text-zinc-700">
              {greeting}
            </span>
          </div>

          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map(({ href, label, icon: Icon }) => (
              <Link
                key={href}
                to={href}
                className={`relative flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 text-sm font-medium group ${
                  isActive(href)
                    ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/20"
                    : "text-zinc-600 hover:text-orange-600 hover:bg-orange-50/60"
                }`}
              >
                <Icon
                  size={18}
                  className={`${
                    isActive(href)
                      ? "text-white"
                      : "text-zinc-500 group-hover:text-orange-600"
                  } transition-colors`}
                />
                <span>{label}</span>

                {isActive(href) && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-white rounded-full shadow-md"></span>
                )}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <UserProfile
              userName={userName}
              userEmail={userEmail}
              userPhoto={userPhoto}
            />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 hover:bg-zinc-100 rounded-lg transition-colors md:hidden"
              aria-label={isOpen ? "Close menu" : "Open menu"}
            >
              {isOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav Drawer */}
        {isOpen && (
          <div className="md:hidden bg-white border-t border-zinc-200 shadow-xl">
            {navLinks.map(({ href, label, icon: Icon }) => (
              <Link
                key={href}
                to={href}
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all duration-300 ${
                  isActive(href)
                    ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white"
                    : "text-zinc-600 hover:bg-orange-50 hover:text-orange-600"
                }`}
              >
                <Icon
                  size={18}
                  className={`${
                    isActive(href) ? "text-white" : "text-zinc-600"
                  }`}
                />
                <span>{label}</span>
              </Link>
            ))}
          </div>
        )}
      </header>

      <main className="pt-16 min-h-screen">
        <div>
          <Outlet />
        </div>
      </main>

      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
